<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { ref, computed, watch } from "vue";
import { isEmail } from "@/utils/regexp";
import { LoaderCircle } from "lucide-vue-next";
import Modal from "@/components/modals/ModalContainer.vue";
import { storeToRefs } from "pinia";

import Metamask from "/icon-metamask-color.svg?url";
import Google from "/icon-google-color.svg?url";

import { useStoreApp } from "~/stores/storeApp";
const { t, locale } = useI18n();

import { useWeb3AuthStore } from "~/stores/storeWeb3auth";

const web3authStore = useWeb3AuthStore();
const { connectWithGoogle, connectWithEmail, connectWithMetamask, logout } = web3authStore;

const { isConnected } = storeToRefs(web3authStore);

const emit = defineEmits<{
  (event: "onClose"): void;
}>();

const storeApp = useStoreApp();

const loading = ref(false);
const formErrors = ref<string | null>(null);
const email = ref<string>("");

const inputRef = ref<HTMLInputElement | null>(null);
function focusInput() {
  inputRef.value?.focus();
}

const isInputFocused = ref(false);
const focused = computed(() => (isInputFocused.value ? "focused" : ""));

const stateError = computed(() => {
  return "";
  // switch (props.state?.type) {
  //   case "error":
  //     return "!border-red-800";
  //   case "warning":
  //     return "border-orange-500";
  //   case "success":
  //     return "border-green-500";
  //   default:
  //     return "";
  // }
});

const isValidEmail = computed((): boolean => isEmail(email.value));

const connectWith = async (method: "google" | "email" | "metamask") => {
  if (loading.value) return;
  else if (!isValidEmail.value && method === "email") {
    return;
  }
  loading.value = true;
  formErrors.value = null;
  await logout();
  try {
    switch (method) {
      case "google":
        connectWithGoogle();
        break;
      case "metamask":
        await connectWithMetamask();
        break;
      case "email":
        await connectWithEmail(email.value);
        break;
    }
  } catch (error: any) {
    formErrors.value = error.message || "An error occurred";
  } finally {
    loading.value = false;
  }
};

watch(
  [() => isConnected.value, () => loading.value],
  ([newIsConnected, newLoading], [oldIsConnected, oldLoading]) => {
    if (newIsConnected === true && newLoading === false) {
      console.log("User connected, closing modal.", newLoading);
      emit("onClose");
    }
  }
);
</script>

<template>
  <Modal
    bgColor="bg-black"
    modalClasses=""
    :show="storeApp.initialized"
    :showCloseButton="true"
    size="lg"
    title=""
    closeButtonClasses=""
    @on-close="emit('onClose')"
  >
    <div v-if="loading" class="bg-black inset-0 absolute z-50 flex items-center justify-center">
      <div class="flex flex-col items-center gap-4 text-neutral-200">
        <LoaderCircle
          :stroke-opacity="80"
          :stroke-width="1"
          class="animate-spin w-16 h-16 text-blue-600"
        />
        {{ t("_auth.signin.connecting") }}
      </div>
    </div>
    <div class="text-xl text-center font-semibold text-neutral-200 mt-4">
      {{ t("_auth.signin.welcome") }}
    </div>

    <div class="flex w-full flex-col items-center gap-1 my-4 px-5 md:px-10">
      <Button
        class="mt-4 w-full !rounded-md py-2.5 border"
        type="secondary"
        @click="connectWith('google')"
        :loading="loading"
      >
        <div class="flex gap-2 justify-center items-center">
          <img :src="Google" alt="Google" class="h-5 mr-2" />
          {{ t("_auth.signin.continue-with", { provider: "Google" }) }}
        </div>
      </Button>
      <Button
        class="mt-4 w-full !rounded-md py-2.5"
        type="secondary"
        @click="connectWith('metamask')"
        :loading="loading"
      >
        <div class="flex gap-2 justify-center items-center">
          <img :src="Metamask" alt="Google" class="h-5 mr-2" />
          {{ t("_auth.signin.continue-with", { provider: "Metamask" }) }}
        </div>
      </Button>

      <div class="flex items-center gap-4 text-neutral-400 relative w-full justify-center">
        <div class="p-2 bg-black relative z-20 mx-10">{{ t("_auth.signin.or") }}</div>
        <div class="absolute left-0 top-1/2 w-full border-t border-neutral-800 z-10"></div>
      </div>
      <div
        class="rounded-md flex bg-neutral-800 w-full items-stretch py-0.5 border border-transparent mb-1"
        :class="[focused, stateError]"
      >
        <div
          @click="focusInput"
          class="rounded-l-md transition-colors pl-4 py-2 flex gap-1 items-center text-neutral-200 cursor-text w-full"
        >
          <input
            v-model="email"
            ref="inputRef"
            type="text"
            @focus="isInputFocused = true"
            @blur="isInputFocused = false"
            :placeholder="t('_auth.signin.email-placeholder')"
            :disabled="loading"
            class="bg-transparent outline-none flex-1"
            @keydown.enter="connectWith('email')"
          />
        </div>

        <div
          class="px-1 text-sm items-center flex border-black text-neutral-200 cursor-pointer group"
        >
          <div
            @click="connectWith('email')"
            class="p-1 px-3 mr-1 text-neutral-200 rounded-md group-hover:text-white border border-transparent transition"
            :class="{
              'bg-neutral-600 cursor-not-allowed': !isValidEmail || loading,
              ' cursor-pointer bg-blue-700 group-hover:bg-blue-600': isValidEmail && !loading,
            }"
          >
            {{ t("_shared.continue") }}
          </div>
        </div>
      </div>

      <div
        class="w-full text-center text-neutral-500 text-xs mt-4 flex flex-row gap-1 justify-center"
      >
        <div>
          {{ t("_auth.by-continuing.you-agree-with") }}
          <a href="#" target="_blank" class="text-neutral-300 hover:underline">{{
            t("_auth.by-continuing.terms-of-service")
          }}</a>
          {{ t("_auth.by-continuing.and") }}
          <a href="#" target="_blank" class="text-neutral-300 hover:underline">{{
            t("_auth.by-continuing.privacy-policy")
          }}</a>
          {{ t("_auth.by-continuing.of-fungiball") }}
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
input {
  @apply border-transparent bg-transparent outline-none w-full;
  box-shadow: none;
  border-radius: 0;
  &::placeholder {
    @apply text-neutral-500;
  }
  &:focus::placeholder {
    @apply text-grey-80;
  }
}

.focused {
  @apply border-blue-700 text-white;
}
</style>
